import React, { useEffect, useMemo, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Tooltip from '../../../../../components/Tooltip';
import { DetailDataItem, SummaryDataItem } from '../../../types/types';
import { toCurrency } from '../../../../../utils/numbers';
import { Section } from '../../shared/Helpers';
import { CreativePerformance, SortToggle } from './CreativePerformance';
import IconButton from '../../../../../components/IconButton';
import { exportToCSV } from '../../../utils/export';
import { ShareReport } from '../../../../DNA/ShareReport';
import format from 'date-fns/format';
import {
  DARWIN_BLUE,
  DARWIN_BLUE_2,
  DARWIN_NAVY,
  SELECT_STYLES,
} from '../../../../../constants';
import { DataMode, DATASET_MAPPING, getDataModeColumns } from './DataMode';
import { useAnalyticsUrlProps } from '../../../../CreativeAnalytics/PivotReport/hooks';
import { useSearchParams } from '../../../../../components/Hooks/search-params';
import {
  CREATIVE_DATA_CSV_EXPORT,
  REPORT_BUILDER,
} from '../../../../../store/UserActivity/constants';
import { useTrackEvent } from '../../../../CreativeAnalytics/hooks/useTrackEvent';
import { ColumnPicker } from '../../shared/ColumnPicker';
import DisplaySelect from './DisplaySelect';
import { useShareStaticDetailData } from '../../../hooks/useShareStaticDetailData';
import { VisualsByLabel } from './VisualsByLabel';
import { useSaveOverridesApi } from '../../../../CreativeAnalytics/Explorer/api';
import { OverridesSelect } from './Overrides';
import { BasicLoading } from '../../../../../components/Helpers/Loading';
import { DetailDataHeader } from './components/DetailDataHeader';
import { collapseToast, toast } from 'react-toastify';

import {
  useOverridesNavigation,
  useSafeLabel,
  useOverrides,
  useVisualsData,
  useLabelVisualsData,
  useCategoryData,
  filterVisualsBySelectedRow,
} from './hooks';
import { convertFormat1ToFormat2 } from './utils/formatUtils';
import { getColumnOptions } from './utils/metricUtils';
import { DetailDataToolbar } from './Toolbar';
import {
  usePlatform,
  useUserId,
} from '../../../../CreativeAnalytics/CategoryBuilder/selectors';
import { useUploadAssetApi } from '../../../api/upload-asset';
import { CloseIcon } from '../../../../../components/Icons';
import { useGroupByContext } from '../../../contexts/GroupByContext';
import { useAssetUpload } from './hooks/useAssetUpload';
import {
  notifyUploadSuccess,
  notifyUploadError,
  notifyUploadLoading,
} from './components/ToastNotifications';
import { OverridesSection } from './components/OverridesSection';
import Scatter from '../../Scatter';
import { useUrlParams } from '../../../../CreativeAnalytics/hooks/useUrlParams';

interface DetailDataProps {
  selectedRow: SummaryDataItem;
  detail_data: DetailDataItem[];
  columns: any[];
  exportFileName: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  isROAS?: boolean;
  renderAnalyticsToggle?: () => React.ReactNode;
  darwinIqApi?: {
    loading: boolean;
    error?: any;
  };
  darwinIqData?: any;
  mode?: 'creative' | 'data' | 'scatter';
  allowDataMode?: boolean;
  visual_id_data?: any[];
  fetchAnalyticsData?: (refresh?: boolean) => void;
  master_mappings?: any;
  summaryCols?: any[];
  selectedSummaryColumns?: any[];
  summary_data?: any;
  sort?: any;
  setSort?: (sort: any) => void;
  selectedLabel?: string;
  dataByCategory?: any;
  selectedCategoryData?: {
    category_labels?: string[];
  };
  safeSetSelectedLabel?: (
    label: string,
    overrides: any,
    callback: () => void
  ) => void;
  colOpts?: any[];
  ShareModal?: React.ReactNode;
  openShareModal?: () => void;
  visuals?: any[];
  onVisualClick?: (visual: any) => void;
}

interface DatasetConfig {
  columns?: any[];
}

// Constants
const SECTION_STYLE = {
  flex: 1,
  padding: '1rem',
  background: 'white',
  margin: '1rem',
  borderRadius: '5px',
  boxShadow: '3px 3px 2px #e4ebf4',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100%',
};

const baseDetailCols = [
  {
    Header: 'Ad Name',
    accessor: 'ad_name',
    maxWidth: 200,
    Cell: ({ row }) => (
      <a
        href={row.original.s3_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 underline"
      >
        {row.original.ad_name}
      </a>
    ),
  },
  { Header: 'Ad Name Group', accessor: 'ad_name_group' },
  {
    Header: 'Total Spend',
    accessor: 'total_spend',
    Cell: ({ value }) => toCurrency(value),
  },

  {
    Header: 'Has Chosen Label',
    accessor: 'has_chosen_label',
    Cell: ({ value }) => (value ? 'Yes' : 'No'),
  },
  {
    Header: 'Has Comparison Label',
    accessor: 'has_comparison_label',
    Cell: ({ value }) => (value ? 'Yes' : 'No'),
  },
  {
    Header: 'Total CPC',
    accessor: 'total_cpc',
    Cell: ({ value }) => toCurrency(value),
  },
  {
    Header: 'CVR',
    accessor: 'CVR',
  },
  { Header: 'Total Link Clicks', accessor: 'total_link_clicks' },
];

const roasDetailCols = [
  {
    Header: 'ROAS',
    accessor: 'total_roas',
    Cell: ({ value }) => value?.toFixed(2) || value || '-',
  },
  {
    Header: 'Total Revenue',
    accessor: 'total_revenue',
    Cell: ({ value }) => toCurrency(value),
  },
];

const cpaClientDetailCols = [
  {
    Header: 'Total CPA',
    accessor: 'total_cpa',
    Cell: ({ value }) => toCurrency(value),
  },
  { Header: 'Total Results', accessor: 'total_results' },
];

const useDetailColumns = (isROAS: boolean = false) => {
  return useMemo(() => {
    if (isROAS) {
      return [...baseDetailCols, ...roasDetailCols];
    }

    return [...baseDetailCols, ...cpaClientDetailCols];
  }, [isROAS]);
};

export const DetailData = ({
  selectedRow,
  detail_data,
  exportFileName,
  dateRange,
  isROAS = false,
  renderAnalyticsToggle = () => null,
  darwinIqApi = { loading: false },
  darwinIqData = {},
  mode = 'creative',
  allowDataMode = true,
  visual_id_data = [],
  fetchAnalyticsData = () => {},
  master_mappings,
  summaryCols,
  selectedSummaryColumns,
  summary_data,
  sort,
  setSort,
  selectedLabel,
  dataByCategory,
  selectedCategoryData,
  safeSetSelectedLabel,
  colOpts,
  ShareModal,
  openShareModal,
  visuals,
  runQueryLoading,
  onVisualClick,
}: DetailDataProps) => {
  const { primary_metric } = useUrlParams();
  const saveOverridesApi = useSaveOverridesApi({
    successTitle: 'Success!',
    successMsg: (
      <div className="toast__content">
        <h3 className="toast__title">Success!</h3>
        <h4 className="toast__subtitle">
          Override saved.{' '}
          <span
            style={{
              color: '#e1e3ff',
              cursor: 'pointer',
            }}
            onClick={() => fetchAnalyticsData(true)}
          >
            Refresh
          </span>{' '}
          to see results.
        </h4>
      </div>
    ),
  });

  const account_id = useSelector((state: any) => state.ViewSettings.account_id);
  const platform = usePlatform();
  const user_id = useUserId();
  const trackEvent = useTrackEvent();
  const { setParams } = useSearchParams();

  let isLoading = mode === 'creative' ? runQueryLoading : darwinIqApi.loading;

  const {
    dataset,
    columns: selectedColumnsParam,
    categories: selectedCategories,
  } = useAnalyticsUrlProps();

  const {
    unsavedOverrides,
    resetOverridesState,
    setMode,
    setDataset,
    handleUnsavedOverridesChange,
    resetOverrides,
  } = useOverridesNavigation(mode, dataset);

  const datasetConfig = DATASET_MAPPING[dataset] || {};

  const { selectedCategory, selectedLabelName, category_id, data } =
    useCategoryData(
      selectedLabel,
      darwinIqData,
      datasetConfig,
      selectedCategories
    );

  const uniqueCategoryNames = useMemo(
    () => [
      ...new Set(
        darwinIqData?.category_performance_data.map(
          (item) => item.category_name
        ) ?? []
      ),
    ],
    [darwinIqData?.category_performance_data]
  );

  const categoryExists = uniqueCategoryNames.includes(selectedCategory);

  const { handleOverride } = useOverrides(
    selectedLabel,
    darwinIqData,
    account_id,
    saveOverridesApi
  );

  const { handleUpload } = useAssetUpload({
    account_id,
    platform,
    user_id,
    onSuccess: () => fetchAnalyticsData(true),
  });

  const columns = useDetailColumns(isROAS);

  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 200,
    }),
    []
  );

  const { rows } = useTable(
    { columns, data: detail_data, defaultColumn },
    useSortBy
  );

  const handleColumnChange = (selectedOptions) => {
    setParams({ columns: selectedOptions.join(',') });
  };

  const selectedColumns = getDataModeColumns(
    dataset,
    selectedColumnsParam,
    isROAS
  );

  const dataModeColumns = useMemo(
    () => datasetConfig.columns || [],
    [datasetConfig]
  );

  const visibleColumns = useMemo(
    () =>
      !selectedColumns.length
        ? dataModeColumns
        : dataModeColumns.filter((col) =>
            selectedColumns.includes(col.accessor)
          ),
    [dataModeColumns, selectedColumns]
  );

  const visualsData = useLabelVisualsData(
    darwinIqData,
    selectedLabel,
    dataByCategory
  );

  const visualIdData = useMemo(() => {
    const filtered = visual_id_data.filter(
      filterVisualsBySelectedRow(selectedRow)
    );

    return filtered?.map((obj) => ({
      ...obj,
      array_of_ad_names: obj.array_of_ad_names || obj['Ad Name'],
    }));
  }, [visual_id_data, selectedRow]);

  const current = useMemo(() => {
    if (!selectedLabel) return null;
    return {
      label: convertFormat1ToFormat2(selectedLabel),
      value: convertFormat1ToFormat2(selectedLabel),
    };
  }, [selectedLabel]);

  const options = useMemo(() => {
    if (!selectedLabel) return [];
    return (
      selectedCategoryData?.category_labels?.map((label) => ({
        label,
        value: label,
      })) || []
    );
  }, [selectedCategoryData?.category_labels]);

  const handleExportCSV = () => {
    trackEvent(REPORT_BUILDER, CREATIVE_DATA_CSV_EXPORT, {});
    exportToCSV(rows, exportFileName, [], isROAS);
  };

  const overrideProps = useMemo(
    () => ({
      current,
      options,
      handleOverride,
      loading: saveOverridesApi.loading,
    }),
    [current, options, handleOverride, saveOverridesApi.loading]
  );

  const renderView = (mode) => {
    if (mode === 'creative') {
      return (
        <CreativePerformance
          data={visuals}
          sort={sort}
          setSort={setSort}
          columnOptions={colOpts}
          visual_id_data={visualIdData}
          master_mappings={master_mappings}
          summaryCols={summaryCols}
          selectedSummaryColumns={selectedSummaryColumns}
          handleUpload={handleUpload}
          visualsPerPageMultiplier={3}
          selectedLabel={selectedLabel}
          onVisualClick={onVisualClick}
        />
      );
    } else if (mode === 'data') {
      return (
        <DataMode
          allowDataMode={allowDataMode}
          darwinIqData={darwinIqData}
          loading={darwinIqApi.loading}
          selectedColumns={selectedColumns}
          visibleColumns={visibleColumns}
          selectedRow={selectedRow}
          selectedLabel={selectedLabel}
          setSelectedLabel={(newLabel) =>
            safeSetSelectedLabel(newLabel, unsavedOverrides, () =>
              resetOverrides()
            )
          }
          dataByCategory={dataByCategory}
        />
      );
    } else if (mode === 'scatter') {
      return (
        <Scatter
          rows={visuals}
          handleSeriesClick={() => {}}
          selectedVisuals={[]}
          filteredVisuals={visuals}
          selectAllVisuals={() => {}}
          allAreSelected={false}
          deselectAllVisuals={() => {}}
          setGroupParams={() => {}}
          primary_metric={primary_metric}
        />
      );
    }
    return null;
  };

  if (isLoading) {
    return <BasicLoading />;
  }

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{ height: '100%', gap: '1rem', margin: '1rem' }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Section
          style={{
            ...SECTION_STYLE,
            margin: '1rem 0',
          }}
          title={
            <DetailDataToolbar
              mode={mode}
              dataset={dataset}
              setMode={setMode}
              setDataset={setDataset}
              selectedColumns={selectedColumns}
              dataModeColumns={dataModeColumns}
              handleColumnChange={handleColumnChange}
              handleExportCSV={handleExportCSV}
              openShareModal={openShareModal}
              renderAnalyticsToggle={renderAnalyticsToggle}
              sort={sort}
              setSort={setSort}
            />
          }
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              paddingTop: '36px',
            }}
          >
            {renderView(mode)}
          </div>
        </Section>
      </div>

      {!!selectedLabel &&
        mode === 'data' &&
        dataset === 'spend_based' &&
        categoryExists && (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Section style={{ ...SECTION_STYLE, margin: '1rem 0' }}>
              <div style={{ flex: 1, minHeight: 0 }}>
                <VisualsByLabel
                  selectedLabel={selectedLabel}
                  visualsData={visualsData}
                  sort={sort}
                  selectedCategoryData={selectedCategoryData}
                  onUnsavedOverridesChange={handleUnsavedOverridesChange}
                  resetOverridesState={resetOverridesState}
                  master_mappings={master_mappings}
                  summaryCols={summaryCols}
                  selectedSummaryColumns={selectedSummaryColumns}
                  handleUpload={handleUpload}
                  overrideProps={overrideProps}
                  onVisualClick={onVisualClick}
                />
              </div>
            </Section>
          </div>
        )}

      {ShareModal}
    </div>
  );
};
